import React, { useState } from 'react'
import type { ValueBetItem, OddsFormat } from '../types'
import { fmtKickoff } from '../utils'
import { useOdds } from '../context/OddsContext'
import { useBetBuilder, type SavedBet } from '../context/BetContext'
import { IoAdd, IoCheckmark, IoChevronDown, IoChevronUp } from 'react-icons/io5'

interface ValueBetsTableProps {
  items: ValueBetItem[]
  expandedId: string | null
  onToggle: (item: ValueBetItem) => void
  getItemKey: (item: ValueBetItem) => string
}

function getConfidenceClass(confidence: string): string {
  switch (confidence) {
    case 'high':
      return 'confidence-high'
    case 'med':
      return 'confidence-med'
    default:
      return 'confidence-low'
  }
}

function getEdgeClass(edge: number): string {
  if (edge > 0) return 'edge-positive'
  if (edge < -2) return 'edge-negative'
  return 'edge-neutral'
}

// Map value bet market type to SavedBet marketType
function mapMarketType(marketType: string): SavedBet['marketType'] {
  const lower = marketType.toLowerCase()
  if (lower.includes('corner')) return 'corners'
  if (lower.includes('shot') && lower.includes('target')) return 'shots_on_target'
  if (lower.includes('shot')) return 'shots'
  if (lower.includes('card') || lower.includes('yellow') || lower.includes('red')) return 'cards'
  if (lower.includes('goal')) return 'goals'
  return 'other'
}

// Determine team type from selection label
function inferTeamType(selectionLabel: string, fixtureLabel: string): SavedBet['teamType'] {
  const lower = selectionLabel.toLowerCase()
  if (lower.includes('total') || lower.includes('match')) return 'total'

  const [homeTeam, awayTeam] = fixtureLabel.split(' vs ').map(t => t.trim().toLowerCase())
  if (homeTeam && lower.includes(homeTeam)) return 'home'
  if (awayTeam && lower.includes(awayTeam)) return 'away'

  return 'total'
}

export function ValueBetsTable({ items, expandedId, onToggle, getItemKey }: ValueBetsTableProps) {
  const { formatOdds } = useOdds()
  const { addBet, removeBet, isBetSaved, openModal } = useBetBuilder()

  const handleToggleBet = (e: React.MouseEvent, bet: ValueBetItem) => {
    e.stopPropagation()
    const betId = `valueBet-${bet.fixture_id}-${bet.market_type}-${bet.selection_label}-${bet.outcome}`
    const isAdded = isBetSaved(betId)
    const isOver = bet.outcome.toLowerCase() === 'over'

    if (isAdded) {
      removeBet(betId)
    } else {
      addBet({
        id: betId,
        fixtureId: bet.fixture_id,
        fixture: bet.fixture_label,
        title: bet.selection_label,
        line: bet.line ?? undefined,
        marketType: mapMarketType(bet.market_type),
        scope: bet.market_category.toLowerCase().includes('player') ? 'player' : 'match',
        teamType: inferTeamType(bet.selection_label, bet.fixture_label),
        isOver,
        confidence: bet.confidence,
        confidencePct: bet.conviction_pct,
        odds: bet.best_odds,
      })
      openModal()
    }
  }

  return (
    <div className="value-bets-table-container">
      <table className="value-bets-table">
        <thead>
          <tr>
            <th className="col-edge">Edge</th>
            <th className="col-selection">Selection</th>
            <th className="col-fixture">Fixture</th>
            <th className="col-kickoff">Kickoff</th>
            <th className="col-prob">Prob</th>
            <th className="col-odds">Odds</th>
            <th className="col-book">Book</th>
            <th className="col-action"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const key = getItemKey(item)
            const isExpanded = expandedId === key
            const edgeLabel = `${item.edge >= 0 ? '+' : ''}${item.edge.toFixed(1)}%`
            const betId = `valueBet-${item.fixture_id}-${item.market_type}-${item.selection_label}-${item.outcome}`
            const isAdded = isBetSaved(betId)

            return (
              <React.Fragment key={key}>
                <tr
                  className={`value-bet-row ${isExpanded ? 'expanded' : ''}`}
                  onClick={() => onToggle(item)}
                >
                  <td className="col-edge">
                    <span className={`edge-badge ${getEdgeClass(item.edge)}`}>
                      {edgeLabel}
                    </span>
                  </td>
                  <td className="col-selection">
                    <span className="selection-text">{item.selection_label}</span>
                  </td>
                  <td className="col-fixture">
                    <span className="fixture-text">{item.fixture_label}</span>
                    <span className="league-text">{item.league_name}</span>
                  </td>
                  <td className="col-kickoff">{fmtKickoff(item.kickoff_utc)}</td>
                  <td className="col-prob">
                    <span className={`prob-badge ${getConfidenceClass(item.confidence)}`}>
                      {item.conviction_pct}%
                    </span>
                  </td>
                  <td className="col-odds">
                    <span className="odds-value">{formatOdds(item.best_odds)}</span>
                  </td>
                  <td className="col-book">{item.best_bookmaker_name}</td>
                  <td className="col-action">
                    <button
                      className={`table-add-btn ${isAdded ? 'added' : ''}`}
                      onClick={(e) => handleToggleBet(e, item)}
                      title={isAdded ? 'Remove from builder' : 'Add to builder'}
                    >
                      {isAdded ? <IoCheckmark size={14} /> : <IoAdd size={14} />}
                    </button>
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="expanded-row">
                    <td colSpan={8}>
                      <div className="expanded-content">
                        <div className="expanded-stats">
                          <div className="stat-item">
                            <span className="stat-label">Our Probability</span>
                            <span className="stat-value">{item.conviction_pct}%</span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">Implied Probability</span>
                            <span className="stat-value">{item.implied_probability.toFixed(1)}%</span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">Edge</span>
                            <span className={`stat-value ${getEdgeClass(item.edge)}`}>{edgeLabel}</span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">Market</span>
                            <span className="stat-value">{item.market_category}</span>
                          </div>
                        </div>
                        {item.context_summary && (
                          <div className="context-info">
                            <span className="context-label">Context:</span>
                            <span className="context-text">{item.context_summary}</span>
                          </div>
                        )}
                        {item.all_odds && item.all_odds.length > 1 && (
                          <div className="all-odds-section">
                            <span className="all-odds-label">All Bookmakers:</span>
                            <div className="all-odds-list">
                              {item.all_odds.map((bkOdds, idx) => (
                                <span
                                  key={`${bkOdds.bookmaker_slug}-${idx}`}
                                  className={`bookmaker-chip ${idx === 0 ? 'best' : ''}`}
                                >
                                  {bkOdds.bookmaker_name}: {formatOdds(bkOdds.odds)}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {item.why && (
                          <div className="why-section">
                            <span className="why-label">Analysis:</span>
                            <span className="why-text">{item.why}</span>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
